export const ROLLOVER_CATEGORIES = ['AMANDA', 'MARTIN', 'HOLIDAYS'];

const MONTH_KEY_PATTERN = /^(\d{4})-(\d{2})/;

const roundCurrency = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const dateToMonthKey = (date) => date.getFullYear() * 12 + date.getMonth();

export const monthKeyToDate = (key) => new Date(Math.floor(key / 12), key % 12, 1);

export const monthKeyToId = (key) => {
  const date = monthKeyToDate(key);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export const monthIdToKey = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? year * 12 + month - 1 : null;
};

export const transactionMonthKey = (value) => {
  if (typeof value === 'string') {
    const match = value.match(MONTH_KEY_PATTERN);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      if (month >= 1 && month <= 12) return year * 12 + month - 1;
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateToMonthKey(date);
};

export const getFirstTrackedMonthKey = (transactions = []) => {
  const keys = transactions
    .map(transaction => transactionMonthKey(transaction?.date))
    .filter(key => key !== null);

  return keys.length > 0 ? Math.min(...keys) : null;
};

export const calculateBudgetRollover = ({
  transactions = [],
  category,
  baseBudget = 0,
  selectedMonth,
  startMonthKey = getFirstTrackedMonthKey(transactions),
  adjustments = {},
}) => {
  const selectedMonthKey = dateToMonthKey(selectedMonth);
  const normalizedBaseBudget = roundCurrency(Number(baseBudget) || 0);
  const spendingByMonth = new Map();

  transactions.forEach(transaction => {
    if (transaction?.expenseType !== category) return;
    const key = transactionMonthKey(transaction.date);
    if (key === null) return;

    const amount = Number(transaction.gbpAmount) || 0;
    spendingByMonth.set(key, roundCurrency((spendingByMonth.get(key) || 0) + amount));
  });

  const history = [];
  let carryOver = 0;

  if (startMonthKey !== null && startMonthKey < selectedMonthKey) {
    for (let key = startMonthKey; key < selectedMonthKey; key += 1) {
      const spent = spendingByMonth.get(key) || 0;
      const monthId = monthKeyToId(key);
      const automaticChange = roundCurrency(normalizedBaseBudget - spent);
      const hasManualAdjustment = Object.prototype.hasOwnProperty.call(adjustments, monthId)
        && Number.isFinite(Number(adjustments[monthId]));
      const change = hasManualAdjustment
        ? roundCurrency(Number(adjustments[monthId]))
        : automaticChange;
      carryOver = roundCurrency(carryOver + change);
      history.push({
        monthKey: key,
        monthId,
        date: monthKeyToDate(key),
        baseBudget: normalizedBaseBudget,
        spent,
        automaticChange,
        change,
        isManual: hasManualAdjustment,
        balanceAfter: carryOver,
      });
    }
  }

  return {
    baseBudget: normalizedBaseBudget,
    carryOver,
    availableBudget: roundCurrency(normalizedBaseBudget + carryOver),
    currentSpent: spendingByMonth.get(selectedMonthKey) || 0,
    history,
    startMonthKey,
  };
};
